import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  useTheme,
  alpha,
  Card,
  CardContent,
  Stack,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Container,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Warehouse as WarehouseIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  PlaylistAddCheck as OpeningIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
  InventoryOpeningBalance,
  CreateInventoryOpeningBalanceDto,
  UpdateInventoryOpeningBalanceDto,
} from '../../types/inventoryOpeningBalances.types';
import { inventoryOpeningBalancesService } from '../../services/inventoryOpeningBalances.service';
import { warehousesService } from '../../services/warehouses.service';
import { branchesService } from '../../services/branches.service';
import { itemsService, itemUnitsService } from '../../services/items.service';
import { Warehouse } from '../../types/warehouses.types';
import { Branch } from '../../types/branches.types';
import { Item } from '../../types/items.types';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

const translations = {
  en: {
    title: 'Inventory Opening Balance',
    documentDetails: 'Document Details',
    items: 'Items',
    summary: 'Summary',
    documentNumber: 'Document #',
    documentDate: 'Date',
    selectLocation: 'Select Location',
    warehouse: 'Warehouse',
    branch: 'Branch',
    item: 'Item',
    unit: 'Unit',
    quantity: 'Qty',
    unitCost: 'Unit Cost',
    totalCost: 'Total',
    addItem: 'Add Item',
    totalItems: 'Total Items',
    totalValue: 'Total Value',
    save: 'Save',
    cancel: 'Cancel',
    notes: 'Notes',
    saved: 'Saved successfully!',
    saveFailed: 'Failed to save',
    selectWarehouseOrBranch: 'Please select Warehouse or Branch',
    next: 'Next',
    back: 'Back',
  },
  ar: {
    title: 'سند افتتاحي للمخزون',
    documentDetails: 'بيانات السند',
    items: 'الأصناف',
    summary: 'الملخص',
    documentNumber: 'رقم السند',
    documentDate: 'التاريخ',
    selectLocation: 'اختر الموقع',
    warehouse: 'المخزن',
    branch: 'الفرع',
    item: 'الصنف',
    unit: 'الوحدة',
    quantity: 'الكمية',
    unitCost: 'تكلفة الوحدة',
    totalCost: 'الإجمالي',
    addItem: 'إضافة صنف',
    totalItems: 'إجمالي الأصناف',
    totalValue: 'إجمالي القيمة',
    save: 'حفظ',
    cancel: 'إلغاء',
    notes: 'ملاحظات',
    saved: 'تم الحفظ بنجاح!',
    saveFailed: 'فشل الحفظ',
    selectWarehouseOrBranch: 'يرجى اختيار المخزن أو الفرع',
    next: 'التالي',
    back: 'السابق',
  },
};

interface InventoryOpeningBalanceDialogProps {
  open: boolean;
  onClose: () => void;
  openingBalance?: InventoryOpeningBalance;
  onSave: () => void;
}

interface Unit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
}

interface ItemUnit {
  unitId: number;
  unitName: string;
  unitNameAr: string;
  unitCode: string;
  conversionFactor: number;
}

interface DetailLine {
  id: string;
  itemId: number | null;
  item: Item | null;
  unitId: number | null;
  unit: Unit | null;
  availableUnits: ItemUnit[];
  quantity: number;
  quantityStr: string;
  unitCost: number;
  unitCostStr: string;
  totalCost: number;
  notes: string;
}

const InventoryOpeningBalanceDialog: React.FC<InventoryOpeningBalanceDialogProps> = ({
  open,
  onClose,
  openingBalance,
  onSave,
}) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user?.isSuperAdmin) ?? false;
  const isEditMode = !!openingBalance;
  const dateLocked = isEditMode && !isSuperAdmin;

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [activeStep, setActiveStep] = useState(0);
  const steps = [t.documentDetails, t.items, t.summary];

  const [formData, setFormData] = useState({
    documentNumber: '',
    documentDate: new Date().toISOString().split('T')[0],
    warehouseId: null as number | null,
    branchId: null as number | null,
    notes: '',
  });

  const [locationType, setLocationType] = useState<'warehouse' | 'branch' | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const [detailLines, setDetailLines] = useState<DetailLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadMasterData();
      setActiveStep(0);
    }
  }, [open]);

  useEffect(() => {
    if (open && (warehouses.length > 0 || branches.length > 0) && items.length > 0) {
      if (openingBalance) {
        loadOpeningBalance();
      } else {
        loadNewDocumentNumber();
        addNewLine();
      }
    }
  }, [open, openingBalance, warehouses, branches, items]);

  const loadMasterData = async () => {
    try {
      const [warehousesData, branchesData, itemsData] = await Promise.all([
        warehousesService.getAll(),
        branchesService.getAll(),
        itemsService.getAll(),
      ]);
      
      let userBranchIds: number[] = [];
      let userWarehouseIds: number[] = [];
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse token:', e); }
      }
    
    const filteredWarehouses = userWarehouseIds.length === 0 && userBranchIds.length === 0
      ? warehousesData
      : warehousesData.filter((w: Warehouse) => 
          userWarehouseIds.includes(w.warehouseId) || 
          (w.branchId && userBranchIds.includes(w.branchId))
        );
    setWarehouses(filteredWarehouses);
    
    const filteredBranches = userBranchIds.length === 0 && userWarehouseIds.length === 0
      ? branchesData
      : branchesData.filter((b: Branch) => userBranchIds.includes(b.branchId));
    setBranches(filteredBranches);
    
    setItems(itemsData);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const loadOpeningBalance = async () => {
    if (!openingBalance) return;

    setFormData({
      documentNumber: openingBalance.documentNumber,
      documentDate: openingBalance.documentDate.split('T')[0],
      warehouseId: openingBalance.warehouseId || null,
      branchId: openingBalance.branchId || null,
      notes: openingBalance.notes || '',
    });

    if (openingBalance.warehouseId) {
      setLocationType('warehouse');
      const warehouse = warehouses.find(w => w.warehouseId === openingBalance.warehouseId);
      setSelectedWarehouse(warehouse || null);
    } else if (openingBalance.branchId) {
      setLocationType('branch');
      const branch = branches.find(b => b.branchId === openingBalance.branchId);
      setSelectedBranch(branch || null);
    }

    const loadLinesAsync = async () => {
      const linesPromises = openingBalance.details.map(async (detail) => {
        const item = items.find(i => i.itemId === detail.itemId);

        let itemUnits: any[] = [];
        let selectedUnit = null;

        try {
          itemUnits = await itemUnitsService.getItemUnits(detail.itemId);
          selectedUnit = itemUnits.find((u: any) => u.unitId === detail.unitId) || null;
        } catch (error) {
          console.error('Failed to load units:', error);
        }

        return {
          id: `${detail.detailId}`,
          itemId: detail.itemId,
          item: item || null,
          unitId: detail.unitId,
          unit: selectedUnit,
          availableUnits: itemUnits,
          quantity: detail.quantity,
          quantityStr: String(detail.quantity ?? ''),
          unitCost: detail.unitCost,
          unitCostStr: String(detail.unitCost ?? ''),
          totalCost: detail.totalCost,
          notes: detail.notes || '',
        };
      });

      const lines = await Promise.all(linesPromises);
      setDetailLines(lines);
    };

    loadLinesAsync();
  };

  const loadNewDocumentNumber = async () => {
    try {
      const documentNumber = await inventoryOpeningBalancesService.generateDocumentNumber();
      setFormData((prev) => ({ ...prev, documentNumber }));
    } catch (error) {
      console.error('Failed to generate document number:', error);
    }
  };

  const addNewLine = () => {
    const newLine: DetailLine = {
      id: Date.now().toString(),
      itemId: null,
      item: null,
      unitId: null,
      unit: null,
      availableUnits: [],
      quantity: 1,
      quantityStr: '1',
      unitCost: 0,
      unitCostStr: '',
      totalCost: 0,
      notes: '',
    };
    setDetailLines((prev) => [...prev, newLine]);
  };

  const removeLine = (id: string) => {
    setDetailLines((prev) => prev.filter((line) => line.id !== id));
  };

  const handleLocationTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: 'warehouse' | 'branch' | null) => {
    if (newType !== null) {
      setLocationType(newType);
      if (newType === 'warehouse') {
        setSelectedBranch(null);
        setFormData({ ...formData, branchId: null });
      } else {
        setSelectedWarehouse(null);
        setFormData({ ...formData, warehouseId: null });
      }
      setErrors({});
    }
  };

  const handleItemChange = async (lineId: string, item: Item | null) => {
    if (!item) {
      updateLine(lineId, 'item', null);
      updateLine(lineId, 'itemId', null);
      updateLine(lineId, 'availableUnits', []);
      updateLine(lineId, 'unit', null);
      updateLine(lineId, 'unitId', null);
      return;
    }

    try {
      let units = await itemUnitsService.getItemUnits(item.itemId);

      let defaultUnit = units.find(u => u.unitId === item.baseUnitId);
      if (!defaultUnit) {
        defaultUnit = units[0];
      }

      setDetailLines((prev) =>
        prev.map((line) =>
          line.id === lineId
            ? {
                ...line,
                item,
                itemId: item.itemId,
                availableUnits: units,
                unit: defaultUnit || null,
                unitId: defaultUnit?.unitId || null,
              }
            : line
        )
      );
    } catch (error) {
      console.error('Failed to load item units:', error);
    }
  };

  const handleUnitChange = (lineId: string, unit: Unit | null) => {
    updateLine(lineId, 'unit', unit);
    updateLine(lineId, 'unitId', unit?.unitId || null);
  };

  const updateLine = (id: string, field: keyof DetailLine, value: any) => {
    setDetailLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        
        const updatedLine = { ...line, [field]: value };
        
        // Recalculate total cost
        if (field === 'quantity' || field === 'unitCost') {
          updatedLine.totalCost = updatedLine.quantity * updatedLine.unitCost;
        }
        
        return updatedLine;
      })
    );
  };

  const calculateTotals = () => {
    const totalItems = detailLines.filter(l => l.itemId).length;
    const totalValue = detailLines.reduce((sum, line) => sum + (line.quantity * line.unitCost), 0);
    return { totalItems, totalValue };
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.warehouseId && !formData.branchId) {
      newErrors.location = t.selectWarehouseOrBranch;
    }
    if (detailLines.length === 0 || !detailLines.some(l => l.itemId)) {
      newErrors.details = 'At least one item is required';
    }

    detailLines.forEach((line, index) => {
      if (line.itemId) {
        if (!line.unitId) newErrors[`unit_${index}`] = 'Required';
        if (line.quantity <= 0) newErrors[`quantity_${index}`] = 'Must be > 0';
        if (line.unitCost < 0) newErrors[`unitCost_${index}`] = 'Must be >= 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const validLines = detailLines.filter(l => l.itemId);

      if (openingBalance) {
        const details = validLines.map((line, index) => {
          const parsedId = parseInt(line.id);
          // ✅ CRITICAL FIX: parse from raw string to preserve full decimal precision
          const qtyVal = line.quantityStr && line.quantityStr !== '' && line.quantityStr !== '.' ? parseFloat(line.quantityStr) : line.quantity;
          const costVal = line.unitCostStr && line.unitCostStr !== '' && line.unitCostStr !== '.' ? parseFloat(line.unitCostStr) : line.unitCost;
          const detail: any = {
            lineNumber: index + 1,
            itemId: line.itemId!,
            unitId: line.unitId!,
            quantity: qtyVal,
            unitCost: costVal,
          };
          if (!isNaN(parsedId) && parsedId > 0 && parsedId < 2147483647) detail.detailId = parsedId;
          if (line.notes) detail.notes = line.notes;
          return detail;
        });
        const updateDto = {
          openingBalanceId: openingBalance.openingBalanceId,
          documentDate: formData.documentDate,
          notes: formData.notes || '',
          details,
        };
        await inventoryOpeningBalancesService.update(openingBalance.openingBalanceId, updateDto as any);
      } else {
        const createDto: CreateInventoryOpeningBalanceDto = {
          documentNumber: formData.documentNumber,
          documentDate: formData.documentDate,
          warehouseId: formData.warehouseId || undefined,
          branchId: formData.branchId || undefined,
          notes: formData.notes || undefined,
          details: validLines.map((line, index) => {
            const qtyVal = line.quantityStr && line.quantityStr !== '' && line.quantityStr !== '.' ? parseFloat(line.quantityStr) : line.quantity;
            const costVal = line.unitCostStr && line.unitCostStr !== '' && line.unitCostStr !== '.' ? parseFloat(line.unitCostStr) : line.unitCost;
            return {
              lineNumber: index + 1,
              itemId: line.itemId!,
              unitId: line.unitId!,
              quantity: qtyVal,
              unitCost: costVal,
              notes: line.notes || undefined,
            };
          }),
        };
        await inventoryOpeningBalancesService.create(createDto);
      }
      toast.success(t.saved);
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save:', error, error.response?.data);
      const msg = error.response?.data?.message || error.response?.data?.title || t.saveFailed;
      const errors = error.response?.data?.errors;
      toast.error(errors ? `${msg}: ${JSON.stringify(errors)}` : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      documentNumber: '',
      documentDate: new Date().toISOString().split('T')[0],
      warehouseId: null,
      branchId: null,
      notes: '',
    });
    setDetailLines([]);
    setSelectedWarehouse(null);
    setSelectedBranch(null);
    setLocationType(null);
    setErrors({});
    setActiveStep(0);
    onClose();
  };

  const totals = calculateTotals();

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Card elevation={6} sx={{ mb: 3, background: theme.palette.mode === 'dark' ? 'linear-gradient(145deg, #1e3a5f 0%, #2c5282 100%)' : 'linear-gradient(145deg, #10b981 0%, #059669 100%)', color: 'white' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <OpeningIcon sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{formData.documentNumber || '---'}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.documentNumber}</Typography>
                  </Box>
                </Stack>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t.documentDate}
                    value={formData.documentDate ? new Date(formData.documentDate) : null}
                    onChange={(date) => setFormData({ ...formData, documentDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                    format="dd/MM/yyyy"
                    views={['year', 'month', 'day']}
                    openTo="day"
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={dateLocked}
                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.15)', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.9)' }, '& .MuiInputBase-input': { color: 'white' } } } }}
                  />
                </LocalizationProvider>
              </CardContent>
            </Card>

            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>{t.selectLocation}</Typography>

                {!openingBalance && (
                  <ToggleButtonGroup
                    value={locationType}
                    exclusive
                    onChange={handleLocationTypeChange}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="warehouse" sx={{ py: 1.5 }}>
                      <WarehouseIcon sx={{ mr: 1 }} />
                      {t.warehouse}
                    </ToggleButton>
                    <ToggleButton value="branch" sx={{ py: 1.5 }}>
                      <StoreIcon sx={{ mr: 1 }} />
                      {t.branch}
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}

                {locationType === 'warehouse' && (
                  <Autocomplete
                    options={warehouses}
                    getOptionLabel={(option) => `${option.warehouseCode} - ${isRTL ? option.warehouseNameAr : option.warehouseName}`}
                    value={selectedWarehouse}
                    onChange={(_, value) => {
                      setSelectedWarehouse(value);
                      setFormData({ ...formData, warehouseId: value?.warehouseId || null });
                    }}
                    renderInput={(params) => <TextField {...params} label={t.warehouse} error={!!errors.location} helperText={errors.location} />}
                    disabled={!!openingBalance}
                  />
                )}

                {locationType === 'branch' && (
                  <Autocomplete
                    options={branches}
                    getOptionLabel={(option) => `${option.branchCode} - ${isRTL ? option.branchNameAr : option.branchName}`}
                    value={selectedBranch}
                    onChange={(_, value) => {
                      setSelectedBranch(value);
                      setFormData({ ...formData, branchId: value?.branchId || null });
                    }}
                    renderInput={(params) => <TextField {...params} label={t.branch} error={!!errors.location} helperText={errors.location} />}
                    disabled={!!openingBalance}
                  />
                )}

                {!locationType && !openingBalance && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {t.selectWarehouseOrBranch}
                  </Alert>
                )}

                {openingBalance && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1">
                      {openingBalance.warehouseId ? (
                        <><WarehouseIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> {isRTL ? openingBalance.warehouseNameAr : openingBalance.warehouseName}</>
                      ) : (
                        <><StoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> {isRTL ? openingBalance.branchNameAr : openingBalance.branchName}</>
                      )}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Box ref={gridRef}>
            <TableContainer component={Paper} elevation={3}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.item}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.unit}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.quantity}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.unitCost}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.totalCost}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.notes}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailLines.map((line, index) => (
                    <TableRow key={line.id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ minWidth: 250 }}>
                        <Autocomplete
                          options={items}
                          getOptionLabel={(option) => `${option.itemCode} - ${isRTL ? option.itemNameAr : option.itemName}`}
                          value={line.item}
                          onChange={(_, value) => handleItemChange(line.id, value)}
                          renderInput={(params) => <TextField {...params} size="small" error={!!errors[`item_${index}`]} />}
                          disabled={openingBalance?.status === 'Posted'}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Autocomplete
                          options={line.availableUnits}
                          getOptionLabel={(option) => isRTL ? option.unitNameAr : option.unitName}
                          value={line.unit}
                          onChange={(_, value) => handleUnitChange(line.id, value)}
                          renderInput={(params) => <TextField {...params} size="small" error={!!errors[`unit_${index}`]} />}
                          disabled={!line.item || openingBalance?.status === 'Posted'}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={line.quantityStr ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || /^\d*\.?\d*$/.test(v)) {
                              const num = v === '' || v === '.' ? 0 : parseFloat(v);
                              setDetailLines(prev => prev.map(l => l.id === line.id ? { ...l, quantityStr: v, quantity: num, totalCost: num * l.unitCost } : l));
                            }
                          }}
                          size="small"
                          error={!!errors[`quantity_${index}`]}
                          inputProps={{ inputMode: 'decimal' }}
                          sx={{ width: 110 }}
                          disabled={openingBalance?.status === 'Posted'}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={line.unitCostStr ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || /^\d*\.?\d*$/.test(v)) {
                              const num = v === '' || v === '.' ? 0 : parseFloat(v);
                              setDetailLines(prev => prev.map(l => l.id === line.id ? { ...l, unitCostStr: v, unitCost: num, totalCost: l.quantity * num } : l));
                            }
                          }}
                          size="small"
                          error={!!errors[`unitCost_${index}`]}
                          inputProps={{ inputMode: 'decimal' }}
                          sx={{ width: 130 }}
                          disabled={openingBalance?.status === 'Posted'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600} color="primary">
                          {(line.quantity * line.unitCost).toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={line.notes}
                          onChange={(e) => updateLine(line.id, 'notes', e.target.value)}
                          size="small"
                          sx={{ minWidth: 120 }}
                          disabled={openingBalance?.status === 'Posted'}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          onClick={() => removeLine(line.id)} 
                          color="error" 
                          size="small"
                          disabled={openingBalance?.status === 'Posted'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>

            <Button
              onClick={addNewLine}
              startIcon={<AddIcon />} 
              variant="outlined" 
              sx={{ mt: 2 }} 
              fullWidth
              disabled={openingBalance?.status === 'Posted'}
            >
              {t.addItem}
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            <Card elevation={6} sx={{ background: theme.palette.mode === 'dark' ? 'linear-gradient(145deg, #1e3a5f 0%, #2c5282 100%)' : 'linear-gradient(145deg, #10b981 0%, #059669 100%)', color: 'white', mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <InventoryIcon sx={{ fontSize: 60 }} />
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={4}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>{totals.totalItems}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalItems}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>{totals.totalValue.toFixed(3)}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalValue}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>{t.documentDetails}</Typography>
              <Stack spacing={1}>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t.documentNumber}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{formData.documentNumber}</Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t.documentDate}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{formData.documentDate}</Typography>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{t.warehouse}/{t.branch}:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedWarehouse ? `${selectedWarehouse.warehouseCode} - ${isRTL ? selectedWarehouse.warehouseNameAr : selectedWarehouse.warehouseName}` : 
                     selectedBranch ? `${selectedBranch.branchCode} - ${isRTL ? selectedBranch.branchNameAr : selectedBranch.branchName}` : '-'}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>{t.items}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t.item}</TableCell>
                      <TableCell>{t.unit}</TableCell>
                      <TableCell align="right">{t.quantity}</TableCell>
                      <TableCell align="right">{t.unitCost}</TableCell>
                      <TableCell align="right">{t.totalCost}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailLines.filter(l => l.itemId).map((line, index) => (
                      <TableRow key={line.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{line.item ? `${line.item.itemCode} - ${isRTL ? line.item.itemNameAr : line.item.itemName}` : '-'}</TableCell>
                        <TableCell>{line.unit ? (isRTL ? line.unit.unitNameAr : line.unit.unitName) : '-'}</TableCell>
                        <TableCell align="right">{line.quantity}</TableCell>
                        <TableCell align="right">{line.unitCost.toFixed(3)}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600} color="primary">
                            {(line.quantity * line.unitCost).toFixed(3)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <TableCell colSpan={5} align="right">
                        <Typography fontWeight={700}>{t.totalValue}:</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color="primary" variant="h6">
                          {totals.totalValue.toFixed(3)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <TextField
              fullWidth
              label={t.notes}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
              sx={{ mt: 2 }}
              disabled={openingBalance?.status === 'Posted'}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth dir={isRTL ? 'rtl' : 'ltr'} PaperProps={{ sx: { minHeight: '95vh', maxHeight: '95vh', background: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' } }}>
      <Box sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <OpeningIcon sx={{ fontSize: 50 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{t.title}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{openingBalance ? 'Edit' : 'New'}</Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Stack>
        <LinearProgress variant="determinate" value={((activeStep + 1) / steps.length) * 100} sx={{ mt: 2, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: '#fbbf24' } }} />
      </Box>

      <Box sx={{ px: 3, pt: 3, bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white' }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, py: 2, overflow: 'auto' }}>
        <Container maxWidth="xl" disableGutters>
          {renderStepContent()}
        </Container>
      </DialogContent>

      <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : 'white' }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button onClick={() => setActiveStep(prev => Math.max(0, prev - 1))} disabled={activeStep === 0} size="large" variant="outlined" sx={{ minWidth: 120 }}>{t.back}</Button>
          <Stack direction="row" spacing={2}>
            <Button onClick={handleClose} size="large" variant="outlined" sx={{ minWidth: 120 }}>{t.cancel}</Button>
            {activeStep === steps.length - 1 ? (
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                disabled={loading || openingBalance?.status === 'Posted'} 
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />} 
                size="large" 
                sx={{ minWidth: 200, background: 'linear-gradient(45deg, #10b981 30%, #059669 90%)' }}
              >
                {t.save}
              </Button>
            ) : (
              <Button onClick={() => setActiveStep(prev => Math.min(steps.length - 1, prev + 1))} variant="contained" size="large" sx={{ minWidth: 120, background: 'linear-gradient(45deg, #10b981 30%, #059669 90%)' }}>{t.next}</Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default InventoryOpeningBalanceDialog;